import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { Form, Formik } from "formik";
import Column from "@amzn/meridian/column/column";
import Row from "@amzn/meridian/row/row";
import Button from "@amzn/meridian/button";
import { useNavigate } from "react-router-dom";

export const ProgressStepForm: React.FC<{
    children: any;
    formName: any;
    initialValues: any;
    onSubmit: any;
    nextButtonClick?: any;
    formRef?: any;
}> = ({ children, initialValues, onSubmit, formRef, formName }) => {
    const innerFormikRef = useRef(null);
    const [stepNumber, setStepNumber] = useState(0);
    const steps = React.Children.toArray(children);
    const [snapshot, setSnapshot] = useState(initialValues);

    const currentStep: any = steps[stepNumber];
    const totalSteps = steps.length;
    const isLastStep = stepNumber === totalSteps - 1;

    const handleProgressStepUpdate = (values, formName, onValidForm, onInvalidForm) => {
        currentStep.props?.validationSchema
            ?.validate(values)
            .then(() => {
                currentStep?.props.updateFormStep({
                    name: formName,
                    progressState: onValidForm,
                    progressAlert: "none",
                });
            })
            .catch(() => {
                currentStep?.props.updateFormStep({
                    name: formName,
                    progressState: onInvalidForm,
                    progressAlert: "warning",
                });
            });
    };

    const onNextButtonClick = values => {
        setSnapshot(values);
        setStepNumber(Math.min(stepNumber + 1, totalSteps - 1));
        handleProgressStepUpdate(values, currentStep.props?.name, "past", "present");
    };

    const onBackButtonClick = (values, bag) => {
        if (stepNumber == 0) {
            setSnapshot(initialValues);
            bag.resetForm(initialValues);
            "updateFormStep" in currentStep.props
                ? currentStep?.props?.updateFormStep({ resetProgress: true })
                : null;
        } else {
            setSnapshot(values);
            setStepNumber(Math.max(stepNumber - 1, 0));
            handleProgressStepUpdate(values, currentStep.props?.name, "past", "future");
        }
    };

    const handleSubmit = async (values, bag) => {
        if (currentStep.props.onSubmit) {
            await currentStep.props.onSubmit(values, bag);
        }
        if (isLastStep) {
            return onSubmit(values, bag);
        } else {
            bag.setTouched({});
            onNextButtonClick(values);
        }
    };

    useImperativeHandle(formRef, () => {
        return {
            submitButtonHandler: () => innerFormikRef.current.handleSubmit(),
            totalSteps: totalSteps,
            isLastStep: isLastStep,
            steps: steps,
            stepNumber: stepNumber,
            snapshot: snapshot,
            currentFormInfo: innerFormikRef.current,
        };
    }, []);
    const navigate = useNavigate();

    return (
        <Formik
            initialValues={snapshot}
            onSubmit={handleSubmit}
            validationSchema={currentStep.props.validationSchema}
            innerRef={innerFormikRef}
            validateOnChange={false}
            validateOnBlur={false}
        >
            {formik => (
                <Form id={formName}>
                    <Column width={"100%"} spacing={"none"}>
                        <Column>{currentStep}</Column>
                        <Row width={"100%"} alignmentHorizontal={"end"} spacing={"400"}>
                            {stepNumber == 0 && (
                                <Button
                                    type={"secondary"}
                                    onClick={() => {
                                        navigate(-1);
                                    }}
                                >
                                    Go back
                                </Button>
                            )}

                            <Button
                                type={"secondary"}
                                disabled={formik.values === formik.initialValues}
                                onClick={() => {
                                    onBackButtonClick(formik.values, formik);
                                }}
                            >
                                {stepNumber > 0 ? "Back" : "Reset"}
                            </Button>
                            <Button
                                onClick={() => {
                                    formik.handleSubmit();
                                }}
                            >
                                {isLastStep ? "Submit" : "Next"}
                            </Button>
                        </Row>
                    </Column>
                </Form>
            )}
        </Formik>
    );
};

export const FormStep = props => {
    const formStepId = React.useId();
    useEffect(() => {
        props?.updateFormStep
            ? props.updateFormStep({
                  name: props?.name,
                  progressState: "present",
              })
            : null;
    }, []);

    return (
        <Form id={formStepId} name={`${{ ...props?.name }}}`}>
            {props?.children}
        </Form>
    );
};
